/**
 * Defines strategies for managing the cleanup of preloaded media resources.
 *
 * - `NO_CLEANUP`: Preloaded resources are retained and not cleaned up.
 * - `FULL_CLEANUP`: All preloaded resources are cleaned up when no longer needed.
 */
export enum PreloadManagementStrategy {
    
    NO_CLEANUP = 0,
    FULL_CLEANUP = 1   

}

/**
 * Defines strategies for managing the unloading of resources in the player.
 *
 * @remarks
 * This enum is used to specify how preloaded resources should be handled when they are no longer needed.
 *
 * @enum {number}
 * @property {number} NO_OP - No operation is performed; resources are left as-is.
 * @property {number} REMOVE_PRELOADED_RESOURCE - Preloaded resources are removed to free up memory.
 */
export enum UnloadManagementStrategy {
    NO_OP = 0,
    REMOVE_PRELOADED_RESOURCE = 1
}

/**
 * Configuration options for partial media events tracking.
 *
 * @property enable - Indicates whether partial media events tracking is enabled.
 * @property progress_resolution - (Optional) The resolution, in seconds, for tracking media progress events.
 * @property dynamic_viewable_impression - (Optional) Enables dynamic viewable impression tracking.
 * @property dynamic_viewable_impression_cap - (Optional) Maximum number of dynamic viewable impressions to track.
 */
export interface PartialMediaEventsConfig {
    enable: boolean,
    progress_resolution?: number,
    dynamic_viewable_impression?: boolean,
    dynamic_viewable_impression_cap?: number
}

/**
 * Configuration options for media event handling within the player.
 * Extends {@link PartialMediaEventsConfig} to allow partial overrides.
 *
 * @property progress_resolution - The granularity (in seconds or percentage) at which progress events are emitted.
 * @property dynamic_viewable_impression - Enables dynamic tracking of viewable impressions.
 * @property dynamic_viewable_impression_cap - The maximum number of dynamic viewable impressions to track.
 */
export interface MediaEventsConfig extends PartialMediaEventsConfig{
    progress_resolution: number,
    dynamic_viewable_impression: boolean,
    dynamic_viewable_impression_cap: number
}   


/**
 * Configuration for player controls.
 *
 * @property events - Partial configuration for media event handlers.
 * @property usePreferenceQuality - Determines whether to use the user's preferred quality settings.
 */
export interface PlayerControlsConfig {
    events: PartialMediaEventsConfig,
    usePreferenceQuality: boolean,
}